import { Dropdown, Input, Button } from "semantic-ui-react";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { convertPropFromLegacyToCamelCase } from "../../../helpers/GlobalHelper";

const dateOptions = [
  {
    text: "Is equal to",
    value: "eq",
    key: "Is equal to",
  },
  {
    text: "Is not equal to",
    value: "neq",
    key: "Is not equal to",
  },
  {
    text: "Is after or equal to",
    value: "gte",
    key: "Is after or equal to",
  },
  {
    text: "Is after",
    value: "gt",
    key: "Is after",
  },
  {
    text: "Is before or equal to",
    value: "lte",
    key: "Is before or equal to",
  },
  {
    text: "Is before",
    value: "lt",
    key: "Is before",
  },
  {
    text: "Is empty",
    value: "isnull",
    key: "Is null",
  },
  {
    text: "Is not empty",
    value: "isnotnull",
    key: "Is not null",
  }
];

const numberFilterOptions = [
  {
    text: "Is equal to",
    value: "eq",
    key: "Is equal to"
  },
  {
    text: "Is not equal to",
    value: "neq",
    key: "Is not equal to",
  },
  {
    text: "Is greater than or equal to",
    value: "gte",
    key: "Is greater than or equal to"
  },
  {
    text: "Is greater than",
    value: "gt",
    key: "Is greater than"
  },
  {
    text: "Is less than or equal to",
    value: "lte",
    key: "Is less than or equal to"
  },
  {
    text: "Is less than",
    value: "lt",
    key: "Is less than"
  },
  {
    text: "Is empty",
    value: "isnull",
    key: "Is null"
  },
  {
    text: "Is not empty",
    value: "isnotnull",
    key: "Is not null"
  }
];

const textFilterOptions = [
  {
    text: "Is equal to",
    value: "eq",
    key: "Is equal to"
  },
  {
    text: "Is not equal to",
    value: "neq",
    key: "Is not equal to",
  },
  {
    text: "Contains",
    value: "contains",
    key: "Contains"
  },
  {
    text: "Does not contain",
    value: "doesnotcontain",
    key: "Does not contain"
  },
  {
    text: "Starts with",
    value: "startswith",
    key: "Starts with"
  },
  {
    text: "Ends with",
    value: "endswith",
    key: "Ends with"
  },
  {
    text: "Is empty",
    value: "isempty",
    key: "Is empty"
  },
  {
    text: "Is not empty",
    value: "isnotempty",
    key: "Is not empty"
  },
  {
    text: "Is contained in",
    value: "iscontainedin",
    key: "Is contained in"
  }
];

const booleanFilterOptions = [
  {
    text: "Is equal to",
    value: "eq",
    key: "Is equal to"
  },
];

const booleanDropdownOptions = [
  { key: "Yes", value: true, text: "Yes" },
  { key: "No", value: false, text: "No" },
];

const andOrOptions = [
  {
    text: "And",
    value: "and",
    key: "And"
  },
  {
    text: "Or",
    value: "or",
    key: "Or"
  }
];

const operandsOptions = {
  date: dateOptions,
  number: numberFilterOptions,
  string: textFilterOptions,
  boolean: booleanFilterOptions
};

const DateField = ({ value, filterType, onChange, nestedProp, handleKeyDown }) => {
  const dateValue = useMemo(() => {
    if (filterType === "date") {
      return value;
    }

    return undefined;
  }, [value]);

  return <Input
    className="date-input-field"
    type="date"
    size="small"
    name="value"
    value={dateValue}
    fluid
    onChange={({ currentTarget }) =>
      onChange(null, currentTarget, nestedProp)
    }
    onKeyDown={handleKeyDown}
  />;
};

const LmsTableHeaderOperandsValueFields = (
  {
    fieldType,
    onChange,
    formValues,
    nestedProp = 0,
    handleKeyDown
  }
) => {
  const defaultValue = useMemo(() => {
    if (fieldType === "string") {
      return "contains";
    }

    return operandsOptions[fieldType]?.[0]?.value;
  }, [fieldType]);

  return <>
    <div className="operand-field-container">
      <Dropdown
        onChange={(e, data) => onChange(e, data, nestedProp)}
        selection
        options={operandsOptions[fieldType] || []}
        name="operator"
        value={formValues?.[nestedProp]?.operator || defaultValue}
        fluid
        compact
      />
    </div>
    <div>
      {fieldType === "date" && (
        <DateField value={formValues?.[nestedProp]?.value || ""} filterType={fieldType} onChange={onChange} handleKeyDown={handleKeyDown} nestedProp={nestedProp} />
      )}
      {fieldType === "string" && (
        <Input
          className="amount-input-field"
          type="text"
          size="small"
          name="value"
          value={formValues?.[nestedProp]?.value || ""}
          onChange={({ currentTarget }) =>
            onChange(null, currentTarget, nestedProp)
          }
          onKeyDown={handleKeyDown}
          fluid
          labelPosition="left"
        />
      )}
      {fieldType === "number" && (
          <Input
            className="amount-input-field"
            type="number"
            size="small"
            name="value"
            value={formValues?.[nestedProp]?.value || ""}
            onChange={({ currentTarget: { name, value } }) =>
              onChange(null, { name, value: parseFloat(value) } , nestedProp)
            }
            onKeyDown={handleKeyDown}
            fluid
            labelPosition="left"
          />
      )}
    </div>
  </>;
};

const LmsTableHeaderFilterConfigFields = (
  {
    prop,
    fieldType,
    isVisible = false,
    posX = "0",
    posY = "0",
    tableFilterReducerInstance,
    setColumnFilter
  }) => {
  const [formValues, setFormValues] = useState(null as any);
  const [{ currentViewFilters }, dispatch]: any = tableFilterReducerInstance;
  const columnFilterRef = useRef(null as any);
  const handleClose = (clickedProp) => {
    if (!clickedProp || clickedProp !== prop) {
      setColumnFilter({ isVisible: false, posX: "0", posY: "0", prop: "", fieldType: "" });
    }

    setFormValues(null);
  };

  useEffect(() => {
    const handleOutsideSearchClick = (e) => {
      if (columnFilterRef.current && columnFilterRef.current.contains(e.target)) {
        return;
      }
      else {
        handleClose(e.target?.parentElement?.dataset?.prop);
      }
    };

    if (isVisible) {
      columnFilterRef?.current?.setAttribute("listener", "true");
      document.addEventListener("mousedown", handleOutsideSearchClick);
    }

    return () => {
      if (columnFilterRef?.current?.getAttribute("listener") !== "true") {
        document.removeEventListener("mousedown", handleOutsideSearchClick);
      }
    };
  }, [isVisible, columnFilterRef]);

  useEffect(() => {
    if (currentViewFilters?.filters && prop) {
      const filterValues = currentViewFilters?.filters?.find((filter: any) => convertPropFromLegacyToCamelCase(filter.filters[0]?.field) === prop);
      if (filterValues) {
        const initFormValues = filterValues.filters.reduce((acc: any, filter: any, index: number) => {
          return { ...acc, [index]: filter };
        }, {});

        setFormValues({ ...initFormValues, logic: filterValues?.logic });
      }
    }
  }, [prop, fieldType]);

  const onChange: any = (_, { name, value }, nestedProp) => {
    if (nestedProp !== undefined) {
      setFormValues({ ...formValues, [nestedProp]: { ...formValues?.[nestedProp], [name]: value } });
    }
    else {
      setFormValues({ ...formValues, [name]: value });
    }
  };

  const handleFilterData = () => {
    let updatedFilterValues: any[];
    const foundFilter = currentViewFilters?.filters?.find((filter: any) => filter.filters[0]?.field === prop);
    if (foundFilter) {
      updatedFilterValues = currentViewFilters?.filters?.map(cwf => {
        if (cwf?.filters?.[0]?.field === prop) {
          const updatedFilters = [] as any[];
          if (formValues) {
            Object.keys(formValues).forEach(key => {
              if (key !== "logic") {
                const operatorValue = formValues?.[key]?.operator ? formValues?.[key]?.operator : fieldType === "string" ? "contains" : "eq";
                updatedFilters.push({ ...formValues[key], field: prop, operator: operatorValue });
              }
            });
          }

          return { ...cwf, filters: updatedFilters, logic: formValues?.logic };
        }

        return cwf;
      });
    }
    else {
      const formattedFilters = [] as any[];
      if (formValues) {
        Object.keys(formValues).forEach(key => {
          if (key !== "logic") {
            const operatorValue = formValues?.[key]?.operator ? formValues?.[key]?.operator : fieldType === "string" ? "contains" : "eq";
            formattedFilters.push({ ...formValues[key], field: prop, operator: operatorValue });
          }
        });
      }

      const newFilter = {
        logic: formValues?.logic,
        filters: formattedFilters
      };

      updatedFilterValues = [...currentViewFilters?.filters || [], newFilter];
    }

    dispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_FILTERS,
      payload: {
        filters: updatedFilterValues
      }
    });

    handleClose(null);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleFilterData();
    }
  };

  const handleClearFilter = () => {
    dispatch({
      type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_FILTERS,
      payload: {
        filters: currentViewFilters?.filters?.filter((filter: any) => filter.filters[0]?.field !== prop)
      }
    });

    setFormValues(null);
    handleClose(null);
  };

  return <>
    {isVisible && <div
      ref={columnFilterRef}
      className="lms-table-column-filter-container"
      style={{
        top: posY,
        left: posX,
        opacity: 1
      }}
    >
      <div className="header-filter-input-container">
        <div className="header-filter-select-dropdown-label">Show items with value that:</div>
        <>
          {fieldType === "boolean" && (
            <>
              <div className="operand-field-container">
                <Dropdown
                  selection
                  options={operandsOptions[fieldType] || []}
                  value="eq"
                  fluid
                  disabled
                  compact
                />
              </div>
              <Dropdown
                onChange={(e, data) => onChange(e, data, 0)}
                selection
                options={booleanDropdownOptions}
                name="value"
                value={formValues?.[0] ? formValues?.[0]?.value : ""}
                compact
              />
            </>
          )}
          {fieldType !== "boolean" && <>
            <LmsTableHeaderOperandsValueFields
              formValues={formValues}
              fieldType={fieldType}
              onChange={onChange}
              nestedProp={0}
              handleKeyDown={handleKeyDown}
            />
            <div className="header-filter-select-and-or-dropdown-container">
              <Dropdown
                onChange={onChange}
                selection
                options={andOrOptions}
                name="logic"
                value={formValues?.logic || "or"}
              />
            </div>
            <LmsTableHeaderOperandsValueFields
              formValues={formValues}
              fieldType={fieldType}
              onChange={onChange}
              nestedProp={1}
              handleKeyDown={handleKeyDown}
            />
          </>
          }

          <div className="header-filter-confirm-buttons-container">
            <Button
              className="header-filter-confirm-button"
              onClick={handleFilterData}
              fluid
            >
              Filter
            </Button>
            <Button
              className="header-filter-clear-button"
              onClick={handleClearFilter}
              fluid
            >
              Clear
            </Button>
          </div>
        </>
      </div>
    </div>}
  </>;
};

export default LmsTableHeaderFilterConfigFields;